import { inject, Injectable } from '@angular/core'
import { AppLabListService, LowercaseDescription } from './app-lab-list.service'
import { lastValueFrom, switchMap } from 'rxjs'
import { AppLabInfo, AppService, GolabAppService } from '@monorepo/golab-generated'
import { LanguageService } from './language.service'
import { AppIdService } from './app-id.service'

/**
 * This service is also used in the ils-player.
 * The app lists are only loaded on demand to prevent unnecessary loading of the lists.
 */
@Injectable({
  providedIn: 'root',
})
export class AppListService extends AppLabListService<LowercaseDescription> {
  private readonly appService = inject(AppService)
  private readonly golabAppService = inject(GolabAppService)
  private readonly languageService = inject(LanguageService)
  private readonly appIdService = inject(AppIdService)

  private supportedAppList: AppLabInfo[] = []

  private readonly appIdToDescriptionMap: Map<string, LowercaseDescription> = new Map()

  constructor() {
    super()
  }

  protected override startLoadList(): void {
    this.getAppLists()
  }

  async getSupportedAppList(): Promise<AppLabInfo[]> {
    if (this.supportedAppList.length === 0) {
      this.supportedAppList = await lastValueFrom(this.appService.appControllerFindSupportedAppList())
    }
    return this.supportedAppList
  }

  getAppDescriptionByAppId(appId: string): LowercaseDescription | undefined {
    return this.appIdToDescriptionMap.get(appId.toLowerCase())
  }

  protected override parseDescriptionList(descriptionList: LowercaseDescription[]): void {
    super.parseDescriptionList(descriptionList)
    for (const description of descriptionList) {
      const appId = this.appIdService.getAppId(description.embedLink)
      if (appId) {
        this.appIdToDescriptionMap.set(appId.toLowerCase(), description)
      } else {
        console.warn(`Could not extract appId from embedLink: ${description.embedLink}`)
      }
    }
  }

  private getAppLists(): void {
    this.languageService
      .getCurrentLanguage$()
      .pipe(
        switchMap((language) => {
          return this.appService.appControllerFindEditorAppList({ language })
        }),
      )
      .subscribe(this.appLabListSubscriber())

    this.languageService
      .getCurrentLanguage$()
      .pipe(
        switchMap((language) => {
          return this.golabAppService.golabAppControllerGetNotSupportedAppList({ language })
        }),
      )
      .subscribe(this.appLabNotSupportedListSubscriber())
  }
}
